/**
 * Sprint Funnel · #4 — Onboarding del negocio.
 *
 * /admin/onboarding
 *   · Barra de progreso (X/5 pasos)
 *   · 5 pasos con check automático + CTA
 *   · Botón "Marcar como compartido" + Copy URL del perfil público
 *   · Botón "Cerrar onboarding" (dismiss banner global)
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, BACKEND_URL } from "../lib/api";
import {
  CheckCircle, Circle, ArrowRight, Confetti, ShareNetwork,
  CopySimple, Storefront, ArrowsClockwise,
} from "@phosphor-icons/react";

export default function Onboarding() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const refresh = async () => {
    try {
      const { data: d } = await api.get("/admin/onboarding/status");
      setData(d);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { refresh(); }, []);

  const markShared = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/admin/onboarding/share-done", {});
      setData(d);
    } catch (e) { console.error(e); }
    finally { setBusy(false); }
  };

  const copyPublicUrl = async () => {
    if (!data?.public_url) return;
    const full = `${window.location.origin}${data.public_url}`;
    try {
      await navigator.clipboard.writeText(full);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  if (!data) {
    return <div className="text-sm text-[var(--ag-text-dim)]" data-testid="onboarding-loading">Cargando…</div>;
  }

  const pct = data.progress_pct;
  const complete = data.is_complete;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="onboarding-page">
      <header>
        <p className="text-[11px] uppercase tracking-[0.2em] text-[var(--ag-gold)] font-semibold">
          Bienvenido al ecosistema
        </p>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight mt-1">
          {complete ? "¡Tu negocio está listo!" : "Configura tu negocio en 5 pasos"}
        </h1>
        <p className="text-sm text-[var(--ag-text-dim)] mt-2">
          Completa estos pasos para empezar a recibir leads desde el primer día. Tu prueba gratuita de 30 días empieza ahora.
        </p>
      </header>

      {/* Progress bar */}
      <div className="space-y-2" data-testid="onboarding-progress">
        <div className="flex items-center justify-between text-xs">
          <span className="text-[var(--ag-text-dim)] tabular-nums">
            {data.completed} de {data.total} pasos completados
          </span>
          <span className="font-bold text-[var(--ag-gold)] tabular-nums" data-testid="onboarding-progress-pct">{pct}%</span>
        </div>
        <div className="h-2 w-full bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[var(--ag-gold)] to-[var(--ag-gold-strong)] transition-all duration-500"
            style={{ width: `${pct}%` }}
            data-testid="onboarding-progress-bar"
          />
        </div>
      </div>

      {complete && (
        <div className="p-4 border border-emerald-500/40 bg-emerald-500/10 rounded-lg flex items-start gap-3" data-testid="onboarding-complete-banner">
          <Confetti size={24} weight="fill" className="text-emerald-300 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-emerald-300">¡Configuración completa!</h3>
            <p className="text-xs text-emerald-200/80 mt-1">
              Tu perfil ya está optimizado. Comparte tu URL pública y empieza a recibir leads.
            </p>
          </div>
        </div>
      )}

      {/* Steps list */}
      <div className="space-y-2" data-testid="onboarding-steps">
        {data.steps.map((step, idx) => (
          <StepCard key={step.id} index={idx + 1} step={step} onMarkShared={step.id === "share_profile" ? markShared : null} busy={busy} />
        ))}
      </div>

      {/* Public URL + share helper */}
      {data.public_url && (
        <section className="border border-[var(--ag-gold)]/30 bg-[var(--ag-gold)]/5 rounded-lg p-4 space-y-3" data-testid="onboarding-share-section">
          <h3 className="font-semibold flex items-center gap-2 text-[var(--ag-gold)]">
            <ShareNetwork size={18} weight="fill" /> Comparte tu perfil
          </h3>
          <p className="text-xs text-[var(--ag-text-dim)]">
            Esta es tu URL pública. Compártela en redes sociales, WhatsApp, o tu firma de email para empezar a recibir leads.
          </p>
          <div className="flex items-center gap-2">
            <a
              href={`${BACKEND_URL}${data.public_url}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 px-3 py-2 bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] rounded text-xs text-[var(--ag-gold)] hover:bg-[var(--ag-midnight)] transition-colors font-mono tabular-nums truncate"
              data-testid="onboarding-public-url"
            >
              {window.location.origin}{data.public_url}
            </a>
            <button
              type="button"
              onClick={copyPublicUrl}
              className="px-3 py-2 bg-[var(--ag-gold)] text-[var(--ag-midnight)] rounded text-xs font-semibold hover:bg-[var(--ag-gold-strong)] inline-flex items-center gap-1.5"
              data-testid="onboarding-copy-url"
            >
              <CopySimple size={14} /> {copied ? "¡Copiado!" : "Copiar"}
            </button>
          </div>
        </section>
      )}

      <div className="flex items-center gap-2 pt-2">
        <button
          type="button"
          onClick={refresh}
          className="flex items-center gap-1.5 px-3 py-2 text-xs text-[var(--ag-text-dim)] hover:text-white border border-[var(--ag-border)] rounded transition-colors"
          data-testid="onboarding-refresh-btn"
        >
          <ArrowsClockwise size={14} /> Refrescar
        </button>
        <Link
          to="/"
          className="text-xs text-[var(--ag-text-dim)] hover:text-white px-3 py-2 underline-offset-4 hover:underline"
          data-testid="onboarding-skip-link"
        >
          Saltar y volver al dashboard
        </Link>
      </div>
    </div>
  );
}

function StepCard({ index, step, onMarkShared, busy }) {
  return (
    <div
      className={`flex items-start gap-3 p-4 border rounded-lg transition-colors ${
        step.done
          ? "border-emerald-500/30 bg-emerald-500/5"
          : "border-[var(--ag-border)] bg-[var(--ag-midnight-soft)]/40 hover:border-[var(--ag-gold)]/40"
      }`}
      data-testid={`onboarding-step-${step.id}`}
    >
      <div className="flex-shrink-0 mt-0.5">
        {step.done ? (
          <CheckCircle size={22} weight="fill" className="text-emerald-400" data-testid={`step-${step.id}-icon-done`} />
        ) : (
          <Circle size={22} className="text-[var(--ag-text-dim)]" data-testid={`step-${step.id}-icon-pending`} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)] font-bold tabular-nums">Paso {index}</span>
          <h3 className={`font-semibold text-sm ${step.done ? "text-emerald-200" : "text-[var(--ag-text)]"}`}>{step.label}</h3>
        </div>
        {!step.done && (
          <div className="mt-2">
            {step.id === "share_profile" && onMarkShared ? (
              <button
                type="button"
                onClick={onMarkShared}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--ag-gold)] text-[var(--ag-midnight)] rounded text-xs font-semibold hover:bg-[var(--ag-gold-strong)] disabled:opacity-50"
                data-testid="onboarding-mark-shared-btn"
              >
                <ShareNetwork size={13} weight="fill" /> Marcar como compartido
              </button>
            ) : (
              <Link
                to={step.cta_route}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--ag-gold)]/40 text-[var(--ag-gold)] rounded text-xs font-medium hover:bg-[var(--ag-gold)]/10 transition-colors"
                data-testid={`onboarding-cta-${step.id}`}
              >
                {step.cta_label} <ArrowRight size={12} />
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
